﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { useSettings } from "~base/Settings/SettingsContext";
import {
    ConsilioApiToken,
    TokenRequestMode,
    TokenRequestStatus,
    useAuthToken,
} from "~base/Services/API/AuthToken";
import { SplashPage } from "~base/Pages/Shared/SplashPage";
import { reducer } from "./Store";
import {
    isSameSiteUrl,
    useNavigateService,
} from "~base/Services/NavigateService";
import log from "loglevel";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type ExternalLoginProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ExternalLoginPage: React.FunctionComponent<ExternalLoginProps> = (
    props: ExternalLoginProps
) => {
    const navigate = useNavigateService();
    const [searchParams] = useSearchParams();
    const settings = useSettings();
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const paramReturnUrl =
        searchParams.get("ReturnUrl") ?? searchParams.get("returnUrl");

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("ExternalLoginPage"),
        reducer,
        {
            tryResolveFromHiddenField: true,
            isNavigating: false,
            navigateUri: null,
        }
    );

    const [tokenLoading, __, ___, ____] = useAuthToken({
        resolveFromHiddenField: state.tryResolveFromHiddenField,
        resolveFromAppStorage: false,

        callback: (
            status: TokenRequestStatus,
            mode: TokenRequestMode,
            _: ConsilioApiToken
        ) => {
            log.info(`Token callback - status: ${status} - mode: ${mode}`);

            const returnUrl =
                paramReturnUrl &&
                status == "Success" &&
                isSameSiteUrl(paramReturnUrl)
                    ? paramReturnUrl
                    : serverVirtualPath;

            const redirectUrl =
                status == "Success"
                    ? returnUrl
                    : `${serverVirtualPath}Account/Login?ReturnUrl=${encodeURIComponent(paramReturnUrl)}`;

            dispatch({
                type: "completedLoginAttempt",
                redirectUri: redirectUrl,
                requestMode: mode,
                status: status,
            });
        },
    });

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(state.navigateUri, { replace: true });
        }
    }, [navigate, state.navigateUri]);

    const loading = tokenLoading || state.isNavigating;
    return (
        <SplashPage isLoading={loading} showSplashScreen={false}></SplashPage>
    );
};
